import { Timestamp } from '@/utils/firebase/lazyFirestore'

/**
 * Guards every client write against the two ways non-values have historically
 * reached Firestore (audited 2026-08-17: 730 broken values across 559 documents).
 *
 * 1. Timestamp maps — a Timestamp that went Firestore -> HTTP/JSON -> Firestore
 *    comes back as a plain object, in one of three shapes depending on which SDK
 *    serialized it:
 *      {_seconds, _nanoseconds}                              (firebase-admin)
 *      {seconds, nanoseconds}                                (hand-rolled)
 *      {type: 'firestore/timestamp/1.0', seconds, nanoseconds} (firebase JS SDK)
 *    Written back as-is, the value stops being a date: range queries skip it and
 *    every date parser in the app reads it as garbage. We revive it losslessly.
 *
 * 2. DOM events — binding `@input`/`@change` on a component that only emits
 *    `update:modelValue` lets the listener fall through to the root element, so
 *    the handler receives the raw browser Event and stores it. This is never
 *    recoverable, so we fail loudly instead of persisting nonsense.
 */

const MAX_DEPTH = 8

function isPlainObject(value: unknown): value is Record<string, unknown> {
    if (!value || typeof value !== 'object') return false
    if (Array.isArray(value)) return false
    if (value instanceof Timestamp || value instanceof Date) return false
    // Firestore sentinels (serverTimestamp, FieldValue, GeoPoint, DocumentReference…)
    // are class instances — leave anything that isn't a bare object alone.
    return Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null
}

function asTimestampMap(value: Record<string, unknown>): Timestamp | null {
    const seconds = value._seconds ?? value.seconds
    const nanoseconds = value._nanoseconds ?? value.nanoseconds
    if (typeof seconds !== 'number') return null
    if (nanoseconds !== undefined && typeof nanoseconds !== 'number') return null
    return new Timestamp(seconds, typeof nanoseconds === 'number' ? nanoseconds : 0)
}

function isDomEvent(value: Record<string, unknown>): boolean {
    return 'isTrusted' in value || '_vts' in value
}

export class FirestoreWriteGuardError extends Error {
    constructor(path: string) {
        super(
            `Refusing to write a DOM Event to Firestore at "${path || '<root>'}". ` +
                'A component was most likely wired with the legacy `:value`/`@input` convention — ' +
                'use `@update:model-value` so the handler receives the value, not the event.'
        )
        this.name = 'FirestoreWriteGuardError'
    }
}

export function sanitizeForFirestore<T>(value: T, path = '', depth = 0): T {
    if (depth > MAX_DEPTH || !value || typeof value !== 'object') return value

    if (Array.isArray(value)) {
        return value.map((item, index) => sanitizeForFirestore(item, `${path}[${index}]`, depth + 1)) as unknown as T
    }

    if (!isPlainObject(value)) return value

    if (isDomEvent(value)) throw new FirestoreWriteGuardError(path)

    const revived = asTimestampMap(value)
    if (revived) return revived as unknown as T

    const out: Record<string, unknown> = {}
    for (const [key, item] of Object.entries(value)) {
        out[key] = sanitizeForFirestore(item, path ? `${path}.${key}` : key, depth + 1)
    }
    return out as unknown as T
}
